import { HTMLAttributes, useMemo, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import {
  WearRegularityTitle,
  WearRegularityView,
} from '../../vision-profile-views/wear-regularity-view';
import { IWearRegularityModalProps } from './wear-regularity-modal.models';
import { IFormImperativeHandleProps } from '../../../../../common/form';
import { IVisionProfileData } from '../../../../../../graphql/profile/models/get-vision-profile.models';
import { useCancellablePromise } from '../../../../../../hooks/use-cancellable-promise';
import { useActionsInProgress } from '../../../../../../graphql/preloader/actions/actions-in-progress';
import { promiseErrorCallbacks } from '../../../../../../utils/promise/set-promise-error-callbacks';
import { VpUpdateModal } from '../../../../../common/vp-update-modal';
import { GET_VISION_PROFILE } from '../../../../../../graphql/profile/queries/get-vision-profile';
import { UPDATE_VISION_PROFILE } from '../../../../../../graphql/profile/mutations';
import { TWearRegularityValues } from '../../vision-profile-views/wear-regularity-view/wear-regularity-form';

export const WearRegularityModal = ({
  wearRegularity, onClose, ...props
}: IWearRegularityModalProps): JSX.Element => {
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const { updateQuery } = useQuery<IVisionProfileData>(GET_VISION_PROFILE);
  const [updateVisionProfile] = useMutation(UPDATE_VISION_PROFILE);
  const { makeCancellablePromise, CancelledPromiseOnUnmountError } = useCancellablePromise();
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();

  const defaultValues = useMemo(() => ({ wearRegularity }), []);

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const handleWearRegularityChange = async ({
    wearRegularity: wearRegularityCurrent,
  }: TWearRegularityValues) => {
    addActionInProgress();

    try {
      const { data: { patchVisionProfile } } = await makeCancellablePromise(
        updateVisionProfile({
          variables: {
            visionProfile: {
              wearRegularity: wearRegularityCurrent,
            },
          },
        }),
      );

      updateQuery((prevState) => ({
        visionProfile: {
          ...prevState.visionProfile,
          ...patchVisionProfile,
        },
      }));

      onClose!();
    } catch (error) {
      if (error instanceof CancelledPromiseOnUnmountError) {
        return;
      }

      if (promiseErrorCallbacks.anyError) {
        promiseErrorCallbacks.anyError();
      }
    }

    removeActionInProgress();
  };

  return (
    <VpUpdateModal
      {...props}
      PaperProps={{
        'data-testid': 'vp-wear-regularity-modal',
      } as HTMLAttributes<HTMLDivElement>}
      title={<WearRegularityTitle />}
      content={(
        <WearRegularityView
          ref={formRef}
          formProps={{
            onSubmit: handleWearRegularityChange,
            defaultValues,
          }}
        />
      )}
      onVPUpdate={handleSubmit}
      onClose={onClose}
    />
  );
};
